import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Database, RefreshCw, Plus, Trash2, Eye, Loader2, CheckCircle,
  AlertCircle, Clock, Table2, Search
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function LookupTablesPage() {
  const [lookupTables, setLookupTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCacheDialog, setShowCacheDialog] = useState(false);
  const [cacheData, setCacheData] = useState(null);
  const [cacheSearch, setCacheSearch] = useState('');
  const [stats, setStats] = useState(null);
  const [newTable, setNewTable] = useState({
    server: '', database: '', table: '', columns: '', max_rows: 1000, filter_sql: '', description: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [tablesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/lookup-tables`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/training-stats`, { headers: getAuthHeader() })
      ]);
      setLookupTables(tablesRes.data.lookup_tables || []);
      setStats(statsRes.data.stats || null);
    } catch (err) {
      toast.error('Failed to load lookup tables');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async () => {
    if (!newTable.server || !newTable.database || !newTable.table || !newTable.columns) {
      toast.error('Server, database, table, and columns are required');
      return;
    }
    try {
      const cols = newTable.columns.split(',').map(c => c.trim()).filter(Boolean);
      await axios.post(`${API}/admin/lookup-tables`, {
        ...newTable,
        columns: cols,
        max_rows: parseInt(newTable.max_rows) || 1000,
        filter_sql: newTable.filter_sql || null,
        description: newTable.description || null
      }, { headers: getAuthHeader() });
      toast.success(`Lookup table '${newTable.table}' registered`);
      setShowAddDialog(false);
      setNewTable({ server: '', database: '', table: '', columns: '', max_rows: 1000, filter_sql: '', description: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to register');
    }
  };

  const handleDelete = async (tableName) => {
    if (!window.confirm(`Remove lookup table '${tableName}'?`)) return;
    try {
      await axios.delete(`${API}/admin/lookup-tables/${tableName}`, { headers: getAuthHeader() });
      toast.success(`'${tableName}' removed`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleRefreshAll = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/admin/refresh-lookups`, {}, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        timeout: 120000
      });
      const data = res.data;
      const successCount = data.results?.filter(r => r.status === 'success').length || 0;
      toast.success(`Refreshed ${successCount}/${data.results?.length || 0} tables`);
      if (data.results?.some(r => r.status === 'error')) {
        const errors = data.results.filter(r => r.status === 'error');
        errors.forEach(e => toast.error(`${e.table}: ${e.message}`));
      }
      fetchData();
    } catch (err) {
      toast.error('Refresh failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRefreshing(false);
    }
  };

  const handleViewCache = async (tableName) => {
    try {
      const res = await axios.get(`${API}/admin/lookup-cache/${tableName}`, { headers: getAuthHeader() });
      setCacheData(res.data.cache);
      setCacheSearch('');
      setShowCacheDialog(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No cache data yet — run Refresh first');
    }
  };

  const [seeding, setSeeding] = useState(false);
  const handleSeedSports = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/admin/seed-sports-intelligence`, { domain: 'sports' }, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      toast.success(`Sports intelligence seeded: ${res.data.details?.length || 0} items`);
      fetchData();
    } catch (err) {
      toast.error('Seed failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSeeding(false);
    }
  };

  const filteredCacheValues = cacheData?.values?.filter(row => {
    if (!cacheSearch) return true;
    const searchLower = cacheSearch.toLowerCase();
    return Object.values(row).some(v => String(v).toLowerCase().includes(searchLower));
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto" data-testid="lookup-tables-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Lookup Tables</h1>
          <p className="text-sm text-gray-500 mt-1">
            Cache reference data so the AI can instantly resolve names to IDs without running subqueries
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSeedSports}
            disabled={seeding}
            data-testid="seed-sports-btn"
          >
            {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
            {seeding ? 'Seeding...' : 'Seed Sports Intelligence'}
          </Button>
          <Button
            variant="outline"
            onClick={handleRefreshAll}
            disabled={refreshing || lookupTables.length === 0}
            data-testid="refresh-all-btn"
          >
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {refreshing ? 'Refreshing...' : 'Refresh All Caches'}
          </Button>
          <Button onClick={() => setShowAddDialog(true)} data-testid="add-lookup-btn">
            <Plus className="w-4 h-4 mr-2" />
            Register Table
          </Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Lookup Tables', value: lookupTables.length, icon: Table2 },
            { label: 'Table Rules', value: stats.table_rules, icon: Database },
            { label: 'Corrections', value: stats.learned_corrections, icon: CheckCircle },
            { label: 'Total Training', value: stats.total_training_data, icon: AlertCircle },
          ].map(s => (
            <Card key={s.label} className="bg-gray-50">
              <CardContent className="p-4 flex items-center gap-3">
                <s.icon className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {lookupTables.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Database className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Lookup Tables Registered</h3>
            <p className="text-sm text-gray-500 mb-4">
              Register tables like sportSnkCompetitions, categories, Providers, etc. so the AI can look up values directly.
            </p>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="w-4 h-4 mr-2" /> Register Your First Table
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {lookupTables.map((lt, idx) => (
            <Card key={idx} className="hover:shadow-md transition-shadow" data-testid={`lookup-table-${lt.table}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 text-lg">{lt.table}</h3>
                      {lt.cached_rows > 0 ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">{lt.cached_rows} cached</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Not cached</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{lt.server}.{lt.database}</p>
                    {lt.description && <p className="text-sm text-gray-600 mt-1">{lt.description}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {lt.columns.map(col => (
                        <Badge key={col} variant="secondary" className="text-xs font-mono">{col}</Badge>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      <span>Max: {lt.max_rows} rows</span>
                      {lt.filter_sql && <span>Filter: {lt.filter_sql}</span>}
                      {lt.last_refreshed && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(lt.last_refreshed).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1.5 ml-4">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => handleViewCache(lt.table)}
                      disabled={lt.cached_rows === 0}
                      data-testid={`view-cache-${lt.table}`}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" /> View
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(lt.table)}
                      data-testid={`delete-${lt.table}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Lookup Table Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-white max-w-lg" data-testid="add-lookup-dialog">
          <DialogHeader>
            <DialogTitle>Register Lookup Table</DialogTitle>
            <DialogDescription>
              Register a reference table whose values will be cached for AI context
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Server</label>
                <Input
                  placeholder="e.g., Member"
                  value={newTable.server}
                  onChange={e => setNewTable(p => ({...p, server: e.target.value}))}
                  data-testid="input-server"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Database</label>
                <Input
                  placeholder="e.g., marketMgmt"
                  value={newTable.database}
                  onChange={e => setNewTable(p => ({...p, database: e.target.value}))}
                  data-testid="input-database"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Table Name</label>
              <Input
                placeholder="e.g., sportSnkCompetitions"
                value={newTable.table}
                onChange={e => setNewTable(p => ({...p, table: e.target.value}))}
                data-testid="input-table"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Columns (comma-separated)</label>
              <Input
                placeholder="e.g., id, competitionName"
                value={newTable.columns}
                onChange={e => setNewTable(p => ({...p, columns: e.target.value}))}
                data-testid="input-columns"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Max Rows</label>
                <Input
                  type="number"
                  value={newTable.max_rows}
                  onChange={e => setNewTable(p => ({...p, max_rows: e.target.value}))}
                  data-testid="input-max-rows"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Filter (optional WHERE)</label>
                <Input
                  placeholder="e.g., status = 1"
                  value={newTable.filter_sql}
                  onChange={e => setNewTable(p => ({...p, filter_sql: e.target.value}))}
                  data-testid="input-filter"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Description (optional)</label>
              <Input
                placeholder="e.g., Sports competitions (IPL, cricket, football)"
                value={newTable.description}
                onChange={e => setNewTable(p => ({...p, description: e.target.value}))}
                data-testid="input-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAdd} data-testid="confirm-add-btn">Register Table</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Cache Dialog */}
      <Dialog open={showCacheDialog} onOpenChange={setShowCacheDialog}>
        <DialogContent className="bg-white max-w-4xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="cache-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Table2 className="w-5 h-5" />
              {cacheData?.table} — {cacheData?.row_count} values
            </DialogTitle>
            <DialogDescription>
              {cacheData?.server}.{cacheData?.database} — Last refreshed: {cacheData?.last_refreshed ? new Date(cacheData.last_refreshed).toLocaleString() : 'Never'}
            </DialogDescription>
          </DialogHeader>
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search values..."
              value={cacheSearch}
              onChange={e => setCacheSearch(e.target.value)}
              className="pl-10"
              data-testid="cache-search"
            />
          </div>
          <div className="overflow-auto flex-1 border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  {cacheData?.columns?.map(col => (
                    <th key={col} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCacheValues.slice(0, 200).map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-3 py-1.5 text-xs text-gray-400">{idx + 1}</td>
                    {cacheData?.columns?.map(col => (
                      <td key={col} className="px-3 py-1.5 text-gray-700 font-mono text-xs">{String(row[col] ?? '')}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredCacheValues.length === 0 && (
              <p className="p-6 text-center text-gray-400 text-sm">No matching values</p>
            )}
          </div>
          {filteredCacheValues.length > 200 && (
            <p className="text-xs text-gray-400 mt-1">Showing 200 of {filteredCacheValues.length} results</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
